import { eurosToCents } from 'shared/utils/formatting';
import {
  ContractorCompanyRate,
  UpdateContractorCompanyRate,
} from 'api/services/company/company-service.schema';

const rateAmountFormatter = new Intl.NumberFormat('nl-NL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatRateInput = (rateCents: number | null): string =>
  rateCents === null ? '' : (rateCents / 100).toFixed(2);

export const formatRateDisplay = (rateCents: number | null): string =>
  rateCents === null ? '—' : rateAmountFormatter.format(rateCents / 100);

export const sanitizeRateInput = (value: string): string => {
  const withoutInvalidCharacters = value.replace(/[^\d.,]/g, '');
  const separatorIndex = withoutInvalidCharacters.search(/[.,]/);
  if (separatorIndex === -1) {
    return withoutInvalidCharacters;
  }

  const separator = withoutInvalidCharacters[separatorIndex];
  const whole = withoutInvalidCharacters.slice(0, separatorIndex);
  const fraction = withoutInvalidCharacters
    .slice(separatorIndex + 1)
    .replace(/[.,]/g, '')
    .slice(0, 2);

  return `${whole}${separator}${fraction}`;
};

export const parseRateCents = (value: string): number | null | undefined => {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  if (!/^\d+([.,]\d{1,2})?$/.test(trimmed)) {
    return undefined;
  }

  return eurosToCents(trimmed.replace(',', '.'));
};

export const rateDraftKey = (
  jobTypeId: string,
  employmentType: UpdateContractorCompanyRate['employmentType']
): string => `${jobTypeId}:${employmentType}`;

export const getRateCents = (
  jobType: ContractorCompanyRate,
  employmentType: UpdateContractorCompanyRate['employmentType']
): number | null => {
  switch (employmentType) {
    case 'FREELANCER':
      return jobType.freelancerRateCents;
    case 'TEMPWORKER':
      return jobType.tempWorkerRateCents;
    case 'EMPLOYEE':
      return jobType.employeeRateCents;
  }
};

export const createRateDrafts = (jobTypes: ContractorCompanyRate[]): Record<string, string> => {
  const drafts: Record<string, string> = {};
  for (const jobType of jobTypes) {
    drafts[rateDraftKey(jobType.jobTypeId, 'FREELANCER')] = formatRateInput(
      jobType.freelancerRateCents
    );
    drafts[rateDraftKey(jobType.jobTypeId, 'TEMPWORKER')] = formatRateInput(
      jobType.tempWorkerRateCents
    );
    drafts[rateDraftKey(jobType.jobTypeId, 'EMPLOYEE')] = formatRateInput(
      jobType.employeeRateCents
    );
  }
  return drafts;
};
